import {useProduce} from '../hooks/use-produce.ts';
import {AdditionalParams} from '../hooks/paged-data-table/types.ts';
import {LogSummariesService} from '../api/generated/LogSummariesService.ts';
import {
  PagedDataTableConfig,
  usePagedDataTable,
} from '../hooks/paged-data-table/use-paged-data-table.tsx';
import {Badge, Group, Stack, Switch, Text} from '@mantine/core';
import {LogSummaryGroupListItem} from '../api/generated/index.defs.ts';
import {Link} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faHorizontalRule} from '@fortawesome/pro-regular-svg-icons';
import {ROUTES} from '../routes';
import {DateTime} from 'luxon';
import {BasicPage} from '../components/basic-page.tsx';
import {css} from '@emotion/react';

type LogsListingState = {
  showOnlyImportant: boolean;
  showOnlyUnresolved: boolean;
};

const DEFAULT_STATE: LogsListingState = {
  showOnlyImportant: true,
  showOnlyUnresolved: true,
};

type FetchParams = AdditionalParams<typeof LogSummariesService.getAll>;

export const LogsListing = () => {
  const [state, setState] = useProduce<LogsListingState>(DEFAULT_STATE);

  const additionalParams: FetchParams = {
    onlyImportant: state.showOnlyImportant,
    onlyUnresolved: state.showOnlyUnresolved,
  };

  const pagedDataTable = usePagedDataTable(
    LogSummariesService.getAll,
    TableConfig,
    {
      renderSearch: (searchHandler, DefaultComponent) => (
        <Group>
          <DefaultComponent onSearchChange={searchHandler} />
          <Switch
            checked={state.showOnlyImportant}
            onChange={() => {
              setState(
                (draft) => (draft.showOnlyImportant = !state.showOnlyImportant)
              );
            }}
            label="Show Only Important"
            className="filter-toggle"
          />
          <Switch
            checked={state.showOnlyUnresolved}
            onChange={() => {
              setState(
                (draft) =>
                  (draft.showOnlyUnresolved = !state.showOnlyUnresolved)
              );
            }}
            label="Show Only Unresolved"
            className="filter-toggle"
          />
        </Group>
      ),
      additionalParams,
    }
  );

  return (
    <BasicPage title="Logs" css={styles}>
      {pagedDataTable}
    </BasicPage>
  );
};

export const getStatusColor = (statusCode: number | undefined) => {
  if (!statusCode) return 'blue';

  if (statusCode >= 500) {
    return 'red';
  }

  if (statusCode >= 400) {
    return 'orange';
  }

  return 'blue';
};

const renderPattern = (log: LogSummaryGroupListItem) => {
  const params = {
    status: log.statusCode,
    matchedPattern: log.matchedPattern,
    errorMessages: log.errorMessages,
  };

  const base64Params = btoa(JSON.stringify(params));
  const url = ROUTES.ADMIN.LOGS.GROUPED_DETAIL.buildPath({
    parameters: base64Params,
  });

  return (
    <Link to={url} className="pattern">
      {log.matchedPattern}
    </Link>
  );
};

const TableConfig = PagedDataTableConfig(LogSummariesService.getAll, {
  columns: [
    {
      accessor: 'statusCode',
      render: (log) => (
        <Stack align={'flex-start'} gap={'xs'}>
          <Group gap={'xs'}>
            <Text c={getStatusColor(log.statusCode)}>{log.statusCode}</Text>
            {renderPattern(log)}
          </Group>
          {log.errorMessages || (
            <FontAwesomeIcon icon={faHorizontalRule} color="gray" />
          )}
        </Stack>
      ),
      sortable: true,
      ellipsis: true,
    },
    {
      accessor: 'occurrences',
      sortable: true,
    },
    {
      accessor: 'status',
      render: (log) =>
        log.hasUnhandledOccurrences ? (
          <Badge variant="light" c={'red'}>
            Unresolved
          </Badge>
        ) : (
          <Badge variant="light" c={'green'}>
            Resolved
          </Badge>
        ),
    },
    {
      accessor: 'lastOccurrence/Age',
      title: 'Last Occurrence | Age',
      render: (log) => (
        <>
          {DateTime.fromISO(String(log.lastOccurrenceTimestamp)).toRelative()}{' '}
          {' | '}
          {DateTime.fromISO(String(log.firstOccurrenceTimestamp))
            .toRelative()
            ?.replace(' ago', ' old')}
        </>
      ),
      sortable: true,
    },
  ],
  defaultSort: {
    column: 'lastOccurrenceTimestamp',
    direction: 'DESC',
  },
  searchFieldNames: [
    'statusCode',
    'matchedPattern',
    'occurrences',
    'errorMessages',
  ],
});

const styles = css`
  .filter-toggle {
    margin-left: 2rem;
  }

  .pattern {
    font-size: 14px;
    color: #1e175c;
    margin-left: 14px;
    margin-top: -2px;
  }
`;